// ── Team orders: who did what ───────────────────────────────────────────────
//
// Technicians who share a manager work each other's orders (see
// 20261105170000_team_orders.sql). The database records every checklist
// item ticked or unticked against the signed-in technician, and credits each
// unit to whoever ticked most of its steps (assembly_orders.unit_builders).
// These helpers read that history for display; the credit itself is computed
// in the database so every page agrees on it.

export interface ChecklistEvent {
  id: number;
  assembly_order_id: string;
  unit_number: number;
  item_key: string;
  checked: boolean;
  technician_id: string | null;
  created_at: string;
  technician?: { name: string | null; initials: string | null; alias: string | null } | null;
}

export interface TechRef {
  id: string;
  name: string | null;
  initials: string | null;
  alias?: string | null;
}

type UnitProgress = Record<string, boolean>;

/** The event that last ticked each item still ticked, keyed `${unit}:${item}`. */
export function currentTicks(
  events: ReadonlyArray<ChecklistEvent>,
  progress: Record<string, UnitProgress>,
): Map<string, ChecklistEvent> {
  const latest = new Map<string, ChecklistEvent>();
  const ordered = [...events].sort((a, b) =>
    a.created_at === b.created_at ? a.id - b.id : a.created_at < b.created_at ? -1 : 1);
  for (const e of ordered) latest.set(`${e.unit_number}:${e.item_key}`, e);

  const out = new Map<string, ChecklistEvent>();
  for (const [key, e] of latest) {
    if (e.checked && progress[String(e.unit_number)]?.[e.item_key]) out.set(key, e);
  }
  return out;
}

export interface Contribution {
  technicianId: string;
  steps: number;
  lastAt: string;
}

/**
 * Who ticked a unit's currently-ticked steps, most steps first (ties to the
 * most recent) - the same rule the database uses to credit the unit.
 */
export function unitContributions(
  ticks: Map<string, ChecklistEvent>,
  unit: number,
): Contribution[] {
  const byTech = new Map<string, Contribution>();
  for (const e of ticks.values()) {
    if (e.unit_number !== unit || !e.technician_id) continue;
    const c = byTech.get(e.technician_id) ?? { technicianId: e.technician_id, steps: 0, lastAt: e.created_at };
    c.steps += 1;
    if (e.created_at > c.lastAt) c.lastAt = e.created_at;
    byTech.set(e.technician_id, c);
  }
  return [...byTech.values()].sort((a, b) => b.steps - a.steps || (a.lastAt < b.lastAt ? 1 : -1));
}

/** Every technician who has ticked or unticked anything on the order. */
export function contributorIds(events: ReadonlyArray<ChecklistEvent>): string[] {
  return [...new Set(events.map(e => e.technician_id).filter((id): id is string => !!id))];
}

interface CreditedOrder {
  quantity?: number | null;
  technician_id?: string | null;
  unit_builders?: Record<string, string> | null;
}

/**
 * Units of an order credited to any of `techIds`. With no filter, every unit.
 * Orders with no recorded builders (none should remain after the backfill)
 * fall back to the assignee, as the database does.
 */
export function creditedUnits(order: CreditedOrder, techIds: ReadonlyArray<string> | null): number {
  const qty = Math.max(order.quantity || 1, 1);
  if (!techIds) return qty;
  const builders = order.unit_builders && Object.keys(order.unit_builders).length
    ? Object.values(order.unit_builders)
    : order.technician_id ? Array(qty).fill(order.technician_id) : [];
  return builders.filter(id => techIds.includes(id)).length;
}

/** "Olaf" - alias, then name, then initials. */
export function techLabel(t: Pick<TechRef, 'name' | 'initials' | 'alias'> | null | undefined): string {
  return t?.alias || t?.name || t?.initials || 'Unknown';
}
